export interface SlackMessage {
  channel: string;
  text: string;
  blocks?: any[];
  attachments?: any[];
}

export interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
}

export class SlackWrapper {
  private botToken: string;
  private baseUrl = 'https://slack.com/api';

  constructor(botToken: string) {
    this.botToken = botToken;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      'Authorization': `Bearer ${this.botToken}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!data.ok) {
        throw new Error(`Slack API error: ${data.error || 'Unknown error'}`);
      }

      return data;
    } catch (error) {
      console.error('Slack API request failed:', error);
      throw error;
    }
  }

  // Send a message to a channel
  async sendMessage(message: SlackMessage): Promise<any> {
    const endpoint = '/chat.postMessage';
    return await this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(message)
    });
  }

  // Get list of channels
  async getChannels(): Promise<SlackChannel[]> {
    const endpoint = '/conversations.list';
    const result = await this.makeRequest(endpoint);
    return result.channels || [];
  }

  // Find channel by name
  async findChannelByName(name: string): Promise<SlackChannel | null> {
    const channels = await this.getChannels();
    return channels.find(channel => channel.name === name) || null;
  }

  // Send account creation notification
  async notifyAccountCreated(account: any, options: {
    channel?: string;
    includeHubSpotData?: boolean;
    hubspotData?: any;
  } = {}) {
    const channel = options.channel || '#crm-updates';
    
    const blocks = [
      {
        type: 'header',
        text: {
          type: 'plain_text',
          text: '🏢 New Account Created'
        }
      },
      {
        type: 'section',
        fields: [
          {
            type: 'mrkdwn',
            text: `*Company:* ${account.company_name}`
          },
          {
            type: 'mrkdwn',
            text: `*Domain:* ${account.domain || 'N/A'}`
          },
          {
            type: 'mrkdwn',
            text: `*Industry:* ${account.industry || 'N/A'}`
          },
          {
            type: 'mrkdwn',
            text: `*Revenue:* ${account.annual_revenue ? `$${account.annual_revenue.toLocaleString()}` : 'N/A'}`
          }
        ]
      }
    ];

    // Add HubSpot enrichment info if available
    if (options.includeHubSpotData && options.hubspotData) {
      blocks.push({
        type: 'context',
        elements: [
          {
            type: 'mrkdwn',
            text: '✨ *Enriched with HubSpot data*'
          }
        ]
      } as any);
    }

    blocks.push({
      type: 'context',
      elements: [
        {
          type: 'mrkdwn',
          text: `Created by: CRM System | ${new Date().toLocaleString()}`
        }
      ]
    } as any);

    const message: SlackMessage = {
      channel,
      text: `New account created: ${account.company_name}`,
      blocks
    };

    return await this.sendMessage(message);
  }

  // Send contact creation notification
  async notifyContactCreated(contact: any, account: any, options: {
    channel?: string;
    accountMatchMethod?: string;
  } = {}) {
    const channel = options.channel || '#crm-updates';
    
    const blocks = [
      {
        type: 'header',
        text: {
          type: 'plain_text',
          text: '👤 New Contact Created'
        }
      },
      {
        type: 'section',
        fields: [
          {
            type: 'mrkdwn',
            text: `*Name:* ${contact.first_name} ${contact.last_name}`
          },
          {
            type: 'mrkdwn',
            text: `*Email:* ${contact.email}`
          },
          {
            type: 'mrkdwn',
            text: `*Title:* ${contact.job_title || 'N/A'}`
          },
          {
            type: 'mrkdwn',
            text: `*Company:* ${account.company_name}`
          }
        ]
      }
    ];

    // Add account matching info if available
    if (options.accountMatchMethod) {
      const matchText = {
        'linkedin': '🔗 Matched by LinkedIn URL',
        'domain': '🌐 Matched by domain',
        'created': '🆕 New account created'
      }[options.accountMatchMethod] || 'Matched to existing account';

      blocks.push({
        type: 'context',
        elements: [
          {
            type: 'mrkdwn',
            text: matchText
          }
        ]
      } as any);
    }

    blocks.push({
      type: 'context',
      elements: [
        {
          type: 'mrkdwn',
          text: `Created by: CRM System | ${new Date().toLocaleString()}`
        }
      ]
    } as any);

    const message: SlackMessage = {
      channel,
      text: `New contact created: ${contact.first_name} ${contact.last_name} at ${account.company_name}`,
      blocks
    };

    return await this.sendMessage(message);
  }

  // Send bulk upload summary
  async notifyBulkUploadComplete(summary: {
    type: 'accounts' | 'contacts';
    total: number;
    successful: number;
    errors: number;
    newAccounts?: number;
    hubspotEnriched?: number;
  }, options: {
    channel?: string;
  } = {}) {
    const channel = options.channel || '#crm-updates';
    
    const isAccounts = summary.type === 'accounts';
    const emoji = isAccounts ? '🏢' : '👥';
    const entityType = isAccounts ? 'Accounts' : 'Contacts';
    
    const blocks = [
      {
        type: 'header',
        text: {
          type: 'plain_text',
          text: `${emoji} Bulk Upload Complete - ${entityType}`
        }
      },
      {
        type: 'section',
        fields: [
          {
            type: 'mrkdwn',
            text: `*Total Processed:* ${summary.total}`
          },
          {
            type: 'mrkdwn',
            text: `*Successful:* ${summary.successful}`
          },
          {
            type: 'mrkdwn',
            text: `*Errors:* ${summary.errors}`
          },
          {
            type: 'mrkdwn',
            text: `*Success Rate:* ${Math.round((summary.successful / summary.total) * 100)}%`
          }
        ]
      }
    ];

    // Add additional info for contacts
    if (!isAccounts && summary.newAccounts) {
      blocks.push({
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: `*New Accounts Created:* ${summary.newAccounts}`
        }
      });
    }

    // Add HubSpot enrichment info
    if (summary.hubspotEnriched) {
      blocks.push({
        type: 'context',
        elements: [
          {
            type: 'mrkdwn',
            text: `✨ ${summary.hubspotEnriched} records enriched with HubSpot data`
          }
        ]
      } as any);
    }

    blocks.push({
      type: 'context',
      elements: [
        {
          type: 'mrkdwn',
          text: `Upload completed: ${new Date().toLocaleString()}`
        }
      ]
    } as any);

    const message: SlackMessage = {
      channel,
      text: `Bulk upload complete: ${summary.successful}/${summary.total} ${entityType.toLowerCase()} processed`,
      blocks
    };

    return await this.sendMessage(message);
  }
}

// Helper function to create Slack wrapper with bot token
export async function createSlackWrapper(): Promise<SlackWrapper | null> {
  try {
    let botToken: string | null = null
    
    // Deno (Supabase Edge Functions)
    // @ts-ignore - Deno typing not available in browser build
    if (typeof Deno !== 'undefined' && Deno?.env?.get) {
      // @ts-ignore
      botToken = Deno.env.get('SLACK_BOT_TOKEN') as string | null
      if (botToken) {
        return new SlackWrapper(botToken)
      }
    }
    
    // Node.js / Server-side
    if (!botToken && typeof process !== 'undefined' && process?.env?.SLACK_BOT_TOKEN) {
      botToken = process.env.SLACK_BOT_TOKEN as string
      if (botToken) {
        return new SlackWrapper(botToken)
      }
    }
    
    // Browser / Client-side
    if (!botToken && typeof window !== 'undefined') {
      // Check Vite env variable (for client-side builds)
      if (import.meta.env?.VITE_SLACK_BOT_TOKEN) {
        botToken = import.meta.env.VITE_SLACK_BOT_TOKEN as string
      }
      // Fallback to localStorage
      if (!botToken) {
        try {
          botToken = localStorage.getItem('slack_bot_token')
        } catch {
          // ignore
        }
      }
    }
    
    if (!botToken) {
      console.log('Slack bot token not configured. Set SLACK_BOT_TOKEN (server) or VITE_SLACK_BOT_TOKEN (client) or store in localStorage as "slack_bot_token"')
      return null
    }

    return new SlackWrapper(botToken)
  } catch (error) {
    console.error('Error creating Slack wrapper:', error)
    return null
  }
}

// Helper function to send notification using Supabase Edge Function
export async function sendSlackNotification(
  type: 'account_created' | 'contact_created' | 'bulk_upload_complete' | 'webhook_failure' | 'campaign_launched',
  data: any
): Promise<boolean> {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    
    console.log('Sending Slack notification:', { type, data });
    
    const { data: result, error } = await supabase.functions.invoke('slack-notifications', {
      body: {
        type,
        data
      }
    });

    if (error) {
      console.error('Slack notification edge function error:', error);
      return false;
    }

    if (result?.success) {
      console.log('Slack notification sent successfully');
      return true;
    }

    if (result?.skipped) {
      console.log('Slack notification skipped (not configured)');
      return true; // Not an error, just not configured
    }

    return false;
  } catch (error) {
    console.error('Failed to send Slack notification:', error);
    return false;
  }
}

// Dedicated function for campaign launch notifications
export async function sendCampaignLaunchNotification(
  campaign: {
    id: string;
    name: string;
    client_id: string;
    client_name?: string;
    status: string;
    campaign_type?: string;
    version?: string;
    target_regions?: string[];
    channels?: string[];
    description?: string;
    total_leads?: number;
    created_at?: string;
  },
  clientSlackChannel?: string
): Promise<boolean> {
  try {
    console.log('🚀 Sending campaign launch notification:', campaign);
    
    // Never straight into a client channel (Jamie, 21 Oct 2026): a launch notice reaches a client only when Alex taps
    // "Share with client" on the machine's launch card (onboarding-machine machine/client/launch.py). This keeps the
    // internal campaigns-channel post and drops the client one, for every caller.
    void clientSlackChannel;
    const success = await sendSlackNotification('campaign_launched', {
      campaign,
      clientSlackChannel: undefined
    });
    
    if (success) {
      console.log('✅ Campaign launch notification sent successfully');
    } else {
      console.error('❌ Failed to send campaign launch notification');
    }
    
    return success;
  } catch (error) {
    console.error('💥 Error sending campaign launch notification:', error);
    return false;
  }
}